import { execSync } from "node:child_process";
import { existsSync, mkdirSync, readdirSync, readFileSync } from "node:fs";
import path from "node:path";
import * as p from "@clack/prompts";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import {
  decodeSkaileManifest,
  getGlobalCacheDir,
  parseFrontmatter,
  SKAILE_MANIFEST_FILENAME,
  sourceCacheKey,
  WorkspaceYamlEditor,
} from "@skaile/workspaces/core";
import { Command } from "commander";
import { logErr, logInfo, logOk, S } from "../helpers.ts";

/**
 * Creates the `skaile npx` command group.
 *
 * Compatibility shim for the `npx skills` syntax. The `npx skills add <url>`
 * subcommand clones the remote repo into the canonical sources cache
 * (`<cache>/sources/<slug>`), registers it in the project's `skaile.yaml`
 * `sources[]`, then installs the requested skill via the canonical resolver —
 * building a publisher-qualified ref (`skill:<name>@<publisher>`, publisher
 * from the source's `skaile.yaml` or its GitHub org).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#npx
 */
export function makeNpxCommand(): Command {
  const npx = new Command("npx").description("Compatibility shim for npx skills syntax");

  const skills = npx.command("skills").description("Skill installation from external repos");

  skills
    .command("add <url>")
    .description(
      "Clone a repo, register it in the project's skaile.yaml sources[], and install a skill from it",
    )
    .requiredOption("--skill <name>", "Skill name to install from the repo")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--target <agent>", "Agent framework", "claude-code")
    .option("--global", "Deploy globally")
    .action(async (url: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const projYaml = path.join(projectDir, "skaile.yaml");
      if (!existsSync(projYaml)) {
        logErr(`No skaile.yaml in ${projectDir} — run \`skaile init\` first.`);
        process.exit(1);
      }
      if (!/^(?:https?:|git@|git:)/.test(url)) {
        logErr(
          "npx skills add requires a git URL — resolution runs through the project's sources[].",
        );
        process.exit(1);
      }

      const spinner = p.spinner();

      // 1. Clone into the canonical sources cache (<cache>/sources/<key>) —
      //    the exact location the resolver reads for the project's sources[].
      const key = sourceCacheKey(url);
      const clonePath = path.join(getGlobalCacheDir(), key);
      spinner.start(`Cloning ${url}`);
      try {
        if (!existsSync(clonePath)) {
          mkdirSync(path.dirname(clonePath), { recursive: true });
          execSync(`git clone ${url} ${clonePath}`, { stdio: "ignore" });
        }
        spinner.stop(`Cloned to ${key}`);
      } catch (err) {
        spinner.stop("Clone failed");
        logErr(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }

      // 2. Register the source in the project skaile.yaml.
      const editor = WorkspaceYamlEditor.load(projYaml);
      editor.setSource({ url });
      editor.save();

      // 3. Install the skill via the canonical resolver. The ref must be
      //    publisher-qualified; publisher is the source's declared one, else
      //    its GitHub org.
      const publisher = resolvePublisher(clonePath, url);
      if (!publisher) {
        logErr(
          `Could not determine the publisher for "${opts.skill}". Declare ` +
            "`publisher:` in the source's skaile.yaml, or use a github URL.",
        );
        process.exit(1);
      }
      // The canonical resolver keys skills by directory name; accept the
      // SKILL.md `name:` too and map it to the directory the resolver expects.
      const skillName = resolveSkillName(clonePath, opts.skill);
      if (skillName !== opts.skill) {
        logInfo(`Resolved "${opts.skill}" → directory "${skillName}".`);
      }
      const skillRef = `skill:@${publisher}/${skillName}`;
      const am = new AssetManager({ projectDir, global: opts.global, driverTarget: opts.target });
      spinner.start(`Installing ${skillRef}`);
      try {
        const added = await am.add(skillRef);
        spinner.stop("Done");
        if (added.length === 0) {
          logInfo(`${opts.skill} already deployed or not found in source.`);
        } else {
          for (const a of added) logOk(a);
        }
        logInfo(`Source registered in skaile.yaml — manage via ${S.cmd("skaile source")}.`);
      } catch (err) {
        spinner.stop("Failed");
        logErr((err as Error).message);
        process.exit(1);
      }
    });

  npx.action(() => npx.help());
  skills.action(() => skills.help());

  return npx;
}

/**
 * Resolve the publisher for a freshly-cloned source: a `publisher:` declared in
 * the source's own `skaile.manifest.yaml` wins, else it is inherited from the
 * source URL's GitHub org (mirrors the provenance walker's rule).
 */
function resolvePublisher(clonePath: string, url: string): string | undefined {
  const manifestPath = path.join(clonePath, SKAILE_MANIFEST_FILENAME);
  if (existsSync(manifestPath)) {
    try {
      const { manifest } = decodeSkaileManifest(readFileSync(manifestPath, "utf8"));
      if (typeof manifest.publisher === "string" && manifest.publisher.length > 0) {
        return manifest.publisher;
      }
    } catch {}
  }
  return url.match(/github\.com[/:]([^/]+)\/[^/]+/)?.[1];
}

/**
 * Map a requested skill identifier to the directory name the canonical resolver
 * keys by. An exact `skills/<requested>/` directory wins; otherwise match a
 * SKILL.md whose frontmatter `name:` equals `requested` (the agentskills.io
 * layout lets the declared name differ from the directory). Falls back to
 * `requested` so the resolver emits a clear not-found.
 */
function resolveSkillName(clonePath: string, requested: string): string {
  const skillsDir = path.join(clonePath, "skills");
  if (existsSync(path.join(skillsDir, requested, "SKILL.md"))) return requested;
  if (!existsSync(skillsDir)) return requested;
  for (const entry of readdirSync(skillsDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const md = path.join(skillsDir, entry.name, "SKILL.md");
    if (!existsSync(md)) continue;
    try {
      const { data } = parseFrontmatter(readFileSync(md, "utf8"));
      if (typeof data.name === "string" && data.name === requested) return entry.name;
    } catch {}
  }
  return requested;
}
